/**
 * OAuth callback handler — receives ?code & ?state from Google / Microsoft and
 * exchanges them for a SaarOne JWT.
 *
 * Mounted on:
 *   /auth/google             → Google sign-in callback
 *   /auth/microsoft/callback → Microsoft sign-in callback
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH. The redirect_uri the browser arrived at must EXACTLY
 * match the URL configured in Google/Azure portals — using `window.location`
 * here is fine because we just observe what the provider sent us.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, routeForRole } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle, Warning, Spinner } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OAuthCallback({ provider }) {
  // provider = 'google' | 'microsoft'
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { fetchMe } = useAuth();
  const [state, setState] = useState({ status: "exchanging" });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      const code = params.get("code");
      const stateParam = params.get("state");
      const error = params.get("error");
      if (error) {
        setState({ status: "error", message: params.get("error_description") || error });
        return;
      }
      const stored = sessionStorage.getItem("sso_state");
      const storedProvider = sessionStorage.getItem("sso_provider");
      sessionStorage.removeItem("sso_state");
      sessionStorage.removeItem("sso_provider");
      if (!code) { setState({ status: "error", message: "No authorization code received" }); return; }
      if (stored && stateParam && stored !== stateParam) {
        setState({ status: "error", message: "State mismatch — possible CSRF attempt. Try again." });
        return;
      }
      const useProvider = provider || storedProvider || "google";
      const purpose = sessionStorage.getItem("sso_purpose") || "signin";
      sessionStorage.removeItem("sso_purpose");

      // Branch — calendar opt-in vs sign-in
      if (purpose === "calendar") {
        try {
          await axios.post(
            `${API}/integrations/${useProvider}/callback`,
            { code, state: stateParam },
            { withCredentials: true, headers: { Authorization: `Bearer ${localStorage.getItem("access_token") || ""}` } },
          );
          setState({ status: "calendar_connected", provider: useProvider });
          setTimeout(() => navigate("/app/integrations", { replace: true }), 800);
        } catch (e) {
          setState({ status: "error", message: e?.response?.data?.detail || "Calendar connection failed." });
        }
        return;
      }

      try {
        const r = await axios.post(
          `${API}/auth/${useProvider}/exchange`,
          { code, state: stateParam },
          { withCredentials: true },
        );
        if (r.data.no_account) {
          setState({ status: "no_account", email: r.data.email, name: r.data.name });
          return;
        }
        // Refresh auth context (reads cookies/localStorage)
        if (r.data.access_token) {
          localStorage.setItem("access_token", r.data.access_token);
        }
        const user = await fetchMe?.();
        const role = user?.role || r.data.user?.role;
        setState({ status: "success", role });
        // Brief delay so user sees the success flash
        setTimeout(() => {
          navigate(role === "company_admin" ? "/app" : routeForRole(role || "employee"), { replace: true });
        }, 600);
      } catch (e) {
        setState({ status: "error", message: e?.response?.data?.detail || "Sign-in failed. Please try again." });
      }
    })();
  }, [params, provider, fetchMe, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-100 px-6" data-testid="oauth-callback">
      <Card className="p-8 max-w-md w-full text-center">
        {state.status === "exchanging" && (
          <>
            <Spinner size={36} className="mx-auto text-zinc-700 animate-spin" />
            <div className="mt-3 font-medium text-zinc-900">Completing sign-in…</div>
            <div className="text-sm text-zinc-500 mt-1">One moment, we&apos;re verifying with {provider}.</div>
          </>
        )}
        {state.status === "success" && (
          <>
            <CheckCircle size={36} weight="duotone" className="mx-auto text-emerald-600" />
            <div className="mt-3 font-medium text-zinc-900">Welcome back!</div>
            <div className="text-sm text-zinc-500 mt-1">Taking you to your workspace…</div>
          </>
        )}
        {state.status === "calendar_connected" && (
          <>
            <CheckCircle size={36} weight="duotone" className="mx-auto text-emerald-600" />
            <div className="mt-3 font-medium text-zinc-900">Calendar connected!</div>
            <div className="text-sm text-zinc-500 mt-1">Returning to your integrations…</div>
          </>
        )}
        {state.status === "no_account" && (
          <>
            <Warning size={36} weight="duotone" className="mx-auto text-amber-600" />
            <div className="mt-3 font-medium text-zinc-900">No SaarOne account yet</div>
            <div className="text-sm text-zinc-500 mt-2">
              We couldn&apos;t find a SaarOne account for <strong>{state.email}</strong>.
              You can start a 14-day free trial or ask your company admin to invite you.
            </div>
            <div className="mt-4 flex gap-2 justify-center">
              <Button onClick={() => navigate(`/signup?email=${encodeURIComponent(state.email)}&name=${encodeURIComponent(state.name || "")}`)} data-testid="signup-from-oauth">Start free trial</Button>
              <Button variant="outline" onClick={() => navigate("/login")}>Back to login</Button>
            </div>
          </>
        )}
        {state.status === "error" && (
          <>
            <Warning size={36} weight="duotone" className="mx-auto text-red-600" />
            <div className="mt-3 font-medium text-zinc-900">Sign-in failed</div>
            <div className="text-sm text-red-700 mt-1">{state.message}</div>
            <Button className="mt-4" onClick={() => navigate("/login")}>Back to login</Button>
          </>
        )}
      </Card>
    </div>
  );
}
